import { useMemo, useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faTriangleExclamation,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

import { useDarkMode } from "../../contexts/ThemeContext";
import { usePermissionGuard } from "../../hooks/usePermissionGuard";
import { useReceiverMFCAData } from "../../hooks/useReceiverMFCAData";
import useFlowAnalysisFilters from "../../hooks/useFlowAnalysisFilters";

import LoadingSpinner from "../../components/ui/LoadingSpinner";
import Alert from "../../components/ui/Alert";
import LastUpdatedBadge from "../../components/ui/LastUpdatedBadge";

import PeriodSelector, {
  formatPeriodStamp,
  formatPeriodDuration,
} from "../../components/ReceiverInventory/PeriodSelector";
import KpiStrip from "../../components/ReceiverInventory/KpiStrip";
import InventoryAccordion from "../../components/Inventory/InventoryAccordion";
import RejectionSummaryCard from "../../components/ReceiverInventory/RejectionSummaryCard";
import ReceiverProblemCarousel from "../../components/ReceiverInventory/ReceiverProblemCarousel";
import MaterialMixCharts from "../../components/ReceiverInventory/MaterialMixCharts";
import ReceiverMaterialBalance from "../../components/ReceiverInventory/ReceiverMaterialBalance";
import ReceiverDonorQualityInsights from "../../components/ReceiverInventory/ReceiverDonorQualityInsights";

import { PROBLEM_THRESHOLD_PCT } from "../../utils/inventory/receiverMfcaUtils";

import "../../style/InventoryDashboard.css";
import "../../style/mfca-dashboard.css";

const FlowAnalysis = () => {
  const { t, i18n } = useTranslation();
  const { darkMode } = useDarkMode();
  const { hasPermission, isChecking } = usePermissionGuard(
    "receiver",
    "inventory",
  );

  const filters = useFlowAnalysisFilters();
  const { data, loading, error, lastUpdated, refresh } = useReceiverMFCAData({
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    materials: filters.materials,
  });

  const [initialLoading, setInitialLoading] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setInitialLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  const { perMaterial = [], perDonor = [], counters, deltas } = data || {};

  const problemMaterialItems = useMemo(() => {
    return perMaterial
      .filter((row) => (row.acceptedPct ?? 100) < PROBLEM_THRESHOLD_PCT)
      .sort((a, b) => (a.acceptedPct ?? 100) - (b.acceptedPct ?? 100))
      .map((row) => ({
        id: row.material,
        name: row.material,
        pct: row.acceptedPct ?? 0,
        deliveredLbs: row.deliveredLbs || 0,
        rejectedLbs: row.rejectedLbs || 0,
        topReason: row.topRejectionReason,
      }));
  }, [perMaterial]);

  const problemDonorItems = useMemo(() => {
    return perDonor
      .filter(
        (row) =>
          !row.belowRatingThreshold &&
          (row.acceptanceRate ?? 100) < PROBLEM_THRESHOLD_PCT,
      )
      .sort((a, b) => (a.acceptanceRate ?? 100) - (b.acceptanceRate ?? 100))
      .map((row) => ({
        id: row.donorId || row.donorName,
        name: row.donorName,
        pct: row.acceptanceRate ?? 0,
        deliveredLbs: row.deliveredLbs || 0,
        rejectedLbs: row.rejectedLbs || 0,
        topReason: row.topRejectionReason,
      }));
  }, [perDonor]);

  const ratedDonorCount = useMemo(
    () => perDonor.filter((d) => !d.belowRatingThreshold).length,
    [perDonor],
  );

  if (isChecking) {
    return (
      <LoadingSpinner
        fullPage
        size={50}
        message={t("Checking permissions...")}
      />
    );
  }
  if (!hasPermission) return null;

  if (initialLoading || (loading && !lastUpdated)) {
    return (
      <LoadingSpinner
        fullPage
        size={50}
        message={t("Loading flow analysis...")}
      />
    );
  }

  const hasAnyData = perMaterial.length > 0 || perDonor.length > 0;
  const periodStamp = formatPeriodStamp(
    filters.dateFrom,
    filters.dateTo,
    i18n.language,
  );
  const periodDuration = formatPeriodDuration(
    filters.dateFrom,
    filters.dateTo,
    t,
  );

  return (
    <Box
      className={`material-management-root inventory-root inventory-dashboard-container ${
        darkMode ? "dark" : "light"
      } visible`}
    >
      <Box className="inv-inner" sx={{ px: { xs: 2, sm: 3 }, pb: 4 }}>
        <div className="rl-toolbar">
          <div className="rl-toolbar-left">
            <PeriodSelector
              preset={filters.preset}
              dateFrom={filters.dateFrom}
              dateTo={filters.dateTo}
              onPresetChange={filters.applyPreset}
              onFromChange={filters.setCustomFrom}
              onToChange={filters.setCustomTo}
            />
          </div>
          <div className="rl-toolbar-right">
            <LastUpdatedBadge
              timestamp={lastUpdated}
              onRefresh={refresh}
              isRefreshing={loading}
              variant="receiver"
            />
          </div>
        </div>

        <div className="rl-page-content">
          <div className="rl-page-headline">
            <h2 className="rl-page-date">{periodStamp}</h2>
            {periodDuration && (
              <span className="rl-page-duration">({periodDuration})</span>
            )}
          </div>

          {error && <Alert message={error} severity="error" />}

          <KpiStrip counters={counters} deltas={deltas} loading={loading} />

          {!loading && !hasAnyData && (
            <div className="rl-all-good">
              <FontAwesomeIcon icon={faCircleCheck} />
              <span>{t("No deliveries logged in this period yet.")}</span>
            </div>
          )}

          {hasAnyData && (
            <>
              <div className="rl-mix-row">
                <MaterialMixCharts
                  perMaterial={perMaterial}
                  darkMode={darkMode}
                />
                <RejectionSummaryCard
                  counters={counters}
                  rejectionRateDelta={deltas?.rejectionRate}
                />
              </div>

              <div className="rl-two-col">
                <ReceiverProblemCarousel
                  title={t("Materials Needing Attention")}
                  iconClass="mfca-icon-blue"
                  icon={faTriangleExclamation}
                  totalCount={perMaterial.length}
                  items={problemMaterialItems}
                  emptyCaption={t(
                    "All {{n}} materials at {{threshold}}%+ acceptance",
                    {
                      n: perMaterial.length,
                      threshold: PROBLEM_THRESHOLD_PCT,
                    },
                  )}
                  infoText={t(
                    "Materials with acceptance below {{threshold}}% in this period. Use the breakdown table below to see all materials including those at 100%.",
                    {
                      threshold: PROBLEM_THRESHOLD_PCT,
                    },
                  )}
                />

                <ReceiverProblemCarousel
                  title={t("Donors Needing Attention")}
                  iconClass="mfca-icon-blue"
                  icon={faUsers}
                  totalCount={ratedDonorCount}
                  items={problemDonorItems}
                  emptyCaption={t(
                    "All {{n}} donors at {{threshold}}%+ acceptance",
                    {
                      n: ratedDonorCount,
                      threshold: PROBLEM_THRESHOLD_PCT,
                    },
                  )}
                  infoText={t(
                    "Rated donors (≥ {{minPickups}} pickups) with acceptance below {{threshold}}% in this period. Donors with too few pickups to rate appear in the breakdown table below.",
                    {
                      minPickups: 5,
                      threshold: PROBLEM_THRESHOLD_PCT,
                    },
                  )}
                />
              </div>

              {perMaterial.length > 0 && (
                <InventoryAccordion
                  id="rl-material-breakdown"
                  title={t("Material Breakdown")}
                  defaultExpanded
                >
                  <ReceiverMaterialBalance rows={perMaterial} />
                </InventoryAccordion>
              )}

              {perDonor.length > 0 && (
                <InventoryAccordion
                  id="rl-donor-breakdown"
                  title={t("Donor Breakdown")}
                  defaultExpanded
                >
                  <ReceiverDonorQualityInsights rows={perDonor} />
                </InventoryAccordion>
              )}
            </>
          )}

          <p className="rl-page-methodology">
            {t(
              "Methodology: ISO 14051-aligned Material Flow Analysis (mass) and Material Flow Cost Accounting (value). Estimates, not accounting-grade.",
            )}
          </p>
        </div>
      </Box>
    </Box>
  );
};

export default FlowAnalysis;
